// The detail panel of one case cell, for nouns and pronouns: विभक्ति + वचन, the कारक role and how the case is used,
// the form(s), stem + ending (nouns), the चतुर्थी = षष्ठी link, audio and the Word Explorer.

import type { FC, ReactNode } from 'react'
import type {
    Case,
    Cell,
    Gender,
    Num
} from '@/prakrit/lib/grammar/types'
import type { GridCell } from '@/prakrit/components/grammar/grammar-grid'

import { useState } from 'react'
import { BookOpen, Equal, ScanSearch } from 'lucide-react'
import { cn } from '@/lib/utils'
import { nounFormation } from '@/prakrit/lib/grammar/formation'
import { CASE_LABEL, KARAK, NUMBER_LABEL } from '@/prakrit/lib/grammar/labels'
import { Bi } from '@/prakrit/components/common/bi'
import { AudioButton } from '@/prakrit/components/common/audio-button'
import { useWordPanel } from '@/prakrit/components/common/word-panel'
import { StatusBadge, reviewBadgeKind } from '@/prakrit/components/common/status-badge'
import { FormationSteps } from '@/prakrit/components/grammar/formation-steps'
import { CARD, SECONDARY_BUTTON } from '@/prakrit/components/grammar/labs/lab-ui'
import { CASE_TWIN, CASE_USE } from '@/prakrit/components/grammar/labs/noun-data'

interface CaseCellPanelProps {
    caseName: Case
    number: Num
    cell: GridCell
    /** The engine's entries of this cell (for each form's status and rule). */
    entries: Cell
    /** The विकल्प tab is chosen: the variant forms can be picked too. */
    showAlt: boolean
    /** The table has variant forms, so a cell with one points to the विकल्प tab while सामान्य is chosen. */
    altHint: boolean
    /** Nouns: the stem, so the form is shown as stem + ending. Pronouns leave it out (their forms are learnt as they are). */
    stem?: string
    gender?: Gender
    /** Rule numbers of the whole table, used when a form names none. */
    rules?: string
    /** The same number in the twin case (चतुर्थी ↔ षष्ठी). */
    twin?: GridCell
    onPickTwin?: () => void
    className?: string
}

export const CaseCellPanel: FC<CaseCellPanelProps> = ({
    caseName,
    number,
    cell,
    entries,
    showAlt,
    altHint,
    stem,
    gender,
    rules,
    twin,
    onPickTwin,
    className
}): ReactNode => {
    const { open } = useWordPanel()
    const shown = [...cell.core, ...(showAlt ? cell.alt : [])]
    const [chosen, setChosen] = useState<string | null>(null)
    const form = chosen && shown.includes(chosen) ? chosen : shown[0] ?? ''
    const entry = entries.find(e => e.form === form)
    const isAlt = entry?.status === 'alt'
    const formation = stem && form ? nounFormation(stem, caseName, number, form, gender, entry?.rule) : null
    const twinCase = CASE_TWIN[caseName]
    const rule = entry?.rule ?? rules

    if (!form) {
        return (
            <article className={cn(CARD, className)}>
                <Bi hi='इस खाने में कोई रूप नहीं है।' en='This cell has no form.' />
            </article>
        )
    }

    return (
        <article aria-labelledby='case-cell-title' className={cn(CARD, 'flex flex-col gap-5', className)}>
            <header className='flex flex-col gap-1'>
                <p className='text-xs font-semibold uppercase tracking-[0.08em] text-saffron-strong'>
                    {CASE_LABEL[caseName].en} · {NUMBER_LABEL[number].en}
                </p>
                <h3 id='case-cell-title' lang='hi' className='text-xl font-bold sm:text-2xl'>
                    {CASE_LABEL[caseName].hi} {NUMBER_LABEL[number].hi}
                </h3>
            </header>

            <div className='flex flex-col gap-1 rounded-xl bg-surface-muted/70 px-4 py-3'>
                <span className='text-xs font-medium text-muted-foreground'>
                    <span lang='hi' className='hindi'>कारक</span>
                    <span aria-hidden='true'> · </span>
                    <span lang='en'>role in the sentence</span>
                </span>
                <span className='flex flex-wrap items-baseline gap-x-2'>
                    <span lang='hi' className='hindi text-lg font-semibold'>{KARAK[caseName].hi}</span>
                    <span lang='en' className='text-sm text-muted-foreground'>{KARAK[caseName].en}</span>
                </span>
                <Bi hi={CASE_USE[caseName].hi} en={CASE_USE[caseName].en} className='gap-0.5 text-sm leading-relaxed' />
            </div>

            <div className='flex flex-col gap-2'>
                <span className='text-xs font-medium text-muted-foreground'>
                    <span lang='hi' className='hindi'>{shown.length > 1 ? 'रूप (टैप करके चुनें)' : 'रूप'}</span>
                    <span aria-hidden='true'> · </span>
                    <span lang='en'>{shown.length > 1 ? 'forms (tap one to explain it)' : 'form'}</span>
                </span>
                {shown.length > 1 ? (
                    <div role='group' aria-label='रूप चुनें · Choose a form' className='flex flex-wrap gap-2'>
                        {shown.map(f => {
                            const alt = cell.alt.includes(f)

                            return (
                                <button
                                    key={f}
                                    type='button'
                                    aria-pressed={f === form}
                                    onClick={() => setChosen(f)}
                                    className={cn(
                                        'inline-flex min-h-tap items-center gap-2 rounded-xl border px-3.5 py-1 transition-colors',
                                        f === form ? 'border-prakrit/40 bg-prakrit/8 ring-1 ring-prakrit/20' : 'border-border bg-card hover:border-saffron/60'
                                    )}
                                >
                                    <span lang='pra' className={cn('font-prakrit text-2xl font-semibold', alt ? 'text-terracotta' : 'text-prakrit')}>{f}</span>
                                    {alt && <StatusBadge kind='alt' withEnglish={false} />}
                                </button>
                            )
                        })}
                    </div>
                ) : (
                    <p lang='pra' className='pra pra-display leading-tight'>{form}</p>
                )}
                <div className='flex flex-wrap items-center gap-1.5'>
                    {isAlt ? <StatusBadge kind='alt' /> : <StatusBadge kind={reviewBadgeKind(cell.review)} />}
                </div>
                {altHint && !showAlt && cell.alt.length > 0 && (
                    <p className='text-xs text-muted-foreground'>
                        <span lang='hi' data-ex='hi' className='hindi'>इस खाने का विकल्प रूप भी है: तालिका का “२. विकल्प” टैब देखें।</span>
                        <span aria-hidden='true' className='ex-sep'> · </span>
                        <span lang='en' data-ex='en'>This cell has a variant form too: see the table’s “Variants” tab.</span>
                    </p>
                )}
            </div>

            <div className='flex flex-wrap items-center gap-2'>
                <button type='button' onClick={() => open(form)} className={SECONDARY_BUTTON}>
                    <ScanSearch className='size-4' aria-hidden='true' />
                    <span lang='hi' className='hindi'>शब्द-विवरण</span>
                    <span lang='en' className='text-xs text-muted-foreground'>Word Explorer</span>
                </button>
                <AudioButton text={form} showMissing />
            </div>

            {formation && (
                <section aria-label='शब्द + प्रत्यय · Stem and ending' className='flex flex-col gap-2 border-t border-border pt-4'>
                    <h4 className='text-sm font-semibold'><Bi hi='मूल शब्द + प्रत्यय' en='Stem + ending' layout='inline' /></h4>
                    <FormationSteps formation={formation} size='sm' />
                </section>
            )}

            {twinCase && twin && twin.core.length > 0 && (
                <section aria-label='चतुर्थी = षष्ठी' className='flex flex-col gap-2 rounded-xl border border-saffron/40 bg-saffron/8 px-4 py-3'>
                    <h4 className='flex flex-wrap items-center gap-2 text-sm font-semibold'>
                        <Equal className='size-4 text-saffron-strong' aria-hidden='true' />
                        <span lang='hi' className='hindi'>{CASE_LABEL[twinCase].hi} {NUMBER_LABEL[number].hi} में भी यही रूप</span>
                        <span lang='en' className='font-normal text-muted-foreground'>the same form as {CASE_LABEL[twinCase].en}</span>
                    </h4>
                    <p className='flex flex-wrap items-center gap-x-3 gap-y-1'>
                        <span lang='pra' className='pra pra-md'>{twin.core.join(' / ')}</span>
                        <span lang='hi' className='hindi text-xs text-muted-foreground'>नि.४६</span>
                    </p>
                    {onPickTwin && (
                        <button type='button' onClick={onPickTwin} className={cn(SECONDARY_BUTTON, 'self-start')}>
                            <span lang='hi' className='hindi'>वह खाना देखें</span>
                            <span lang='en' className='text-xs text-muted-foreground'>Show that cell</span>
                        </button>
                    )}
                </section>
            )}

            {rule && (
                <p className='flex flex-wrap items-center gap-2 text-sm'>
                    <BookOpen className='size-4 text-saffron-strong' aria-hidden='true' />
                    <span lang='hi' className='hindi font-semibold'>नियम</span>
                    <span lang='hi' className='hindi rounded-full border border-border bg-card px-2 py-0.5 text-xs'>{rule}</span>
                    <span lang='hi' className='hindi text-xs text-muted-foreground'>प्राकृत स्वयं-शिक्षक</span>
                </p>
            )}
        </article>
    )
}
